import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartCard } from "./chart-card";
import { ProgressList, sortRowsLocally, type SortMode } from "./progress-list";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import {
	Download,
	GraduationCap,
	Trophy,
	TrendingUp,
	ChevronDown,
	ChevronRight,
} from "lucide-react";
import {
	type StatisticsByDepartment,
	type StatisticsByFaculty,
} from "@/lib/statistics-api.service";
import {
	StatisticsFilterControls,
	type StatisticsFilterType,
	type StatisticsFilters,
} from "./StatisticsControls";
import { COLORS, createMajorLabeler, downloadChartPng } from "./utils";
import {
	BAR_VALUE_LABEL,
	NO_ANIMATION,
	CATEGORY_AXIS_EXTRA_HEIGHT,
	CATEGORY_AXIS_HEIGHT,
	WrappedCategoryTick,
	chartMinWidth,
} from "./chart-ticks";
// import { MAJORS } from "@/lib/data-constants"; // MAJORS array has been removed

interface MajorsTabProps {
	departmentStats: StatisticsByDepartment[];
	facultyStats?: StatisticsByFaculty[];
	onFilterChange?: (filter: StatisticsFilterType) => void;
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
	/**
	 * Tab nào đang render component này. Hai góc nhìn từng là hai tab con lồng
	 * bên trong một tab, nhưng mỗi góc nhìn là một câu hỏi riêng — "ngành nào" và
	 * "khoa nào" — nên chúng đứng thành hai tab ngang hàng, mỗi tab một link.
	 */
	view: "majors" | "faculties";
}

const HEADING = {
	majors: { title: "Thống kê theo ngành học", filename: "thong-ke-theo-nganh-hoc" },
	faculties: { title: "Thống kê theo khoa", filename: "thong-ke-theo-khoa-dao-tao" },
} as const;

export default function MajorsTab({
	departmentStats = [],
	facultyStats = [],
	onFilterChange,
	filters = {},
	onFiltersChange,
	view,
}: MajorsTabProps) {
	const departmentRef = useRef<HTMLDivElement>(null);
	const [filterType, setFilterType] = useState<StatisticsFilterType>("total");
	const [expandedFaculties, setExpandedFaculties] = useState<Set<string>>(new Set());
	const [sortBy, setSortBy] = useState<SortMode>("count");
	const heading = HEADING[view];

	// Early return if no data available
	const safeDepartmentStats = departmentStats || [];
	const safeFacultyStats = facultyStats || [];

	// Add fallback UI if no data is available
	if (!safeDepartmentStats.length && !safeFacultyStats.length) {
		return (
			<TabsContent value={view}>
				<div className="space-y-6">
					<div className="flex justify-between items-center">
						<h2 className="text-2xl font-semibold">{HEADING[view].title}</h2>
					</div>
					<Card>
						<CardContent className="p-8 text-center">
							<p className="text-muted-foreground">Không có dữ liệu để hiển thị</p>
						</CardContent>
					</Card>
				</div>
			</TabsContent>
		);
	}

	const handleFilterChange = (newFilter: StatisticsFilterType) => {
		setFilterType(newFilter);
		onFilterChange?.(newFilter);
	};

	const toggleFacultyExpansion = (facultyCode: string) => {
		setExpandedFaculties((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(facultyCode)) {
				newSet.delete(facultyCode);
			} else {
				newSet.add(facultyCode);
			}
			return newSet;
		});
	};

	const toggleAllFaculties = () => {
		if (expandedFaculties.size === sortedFaculties.length) {
			// If all are expanded, collapse all
			setExpandedFaculties(new Set());
		} else {
			// If not all are expanded, expand all
			setExpandedFaculties(new Set(sortedFaculties.map((f) => f?.facultyCode).filter(Boolean)));
		}
	};

	// Tên ngành đến từ `department`, KHÔNG phải từ một bảng tra trong FE.
	//
	// Trước đây hàm này trả về chính mã ngành: bảng `MAJORS` hardcoded trong FE bị
	// bỏ, và chỗ này để lại mã làm "fallback" vĩnh viễn, nên mọi biểu đồ, card và
	// hàng bảng đều hiện `7480101` thay vì "Khoa học máy tính". Server đã map
	// `MaNganh` sang `major.name` của catalog do admin quản lý và trả sẵn trong
	// `department` — chỉ cần đọc nó.
	//
	// Việc đặt nhãn (kể cả chống trùng tên) nằm trong `createMajorLabeler` để
	// kiểm chứng được bằng test — xem chú thích ở đó.
	const getMajorName = createMajorLabeler(safeDepartmentStats);

	// Ngành thuộc khoa nào là do server nói, KHÔNG suy từ mã.
	//
	// Chỗ này từng so `departmentCode.startsWith(facultyCode)`, và nó không bao
	// giờ đúng: mã ngành là mã số ("7480201_DKD") còn mã khoa là chữ ("DC"), nên
	// mọi khoa đều hiện "0 ngành" và bung ra một danh sách rỗng. Cùng lý do,
	// `includes` cũng không dùng được — một mã khoa ngắn có thể tình cờ nằm trong
	// mã ngành của khoa khác và gom nhầm ngành vào.
	const departmentsOfFaculty = (facultyCode: string) =>
		safeDepartmentStats.filter((dept) => dept?.facultyCode === facultyCode);

	// Use faculty data from API instead of calculating from department data
	const sortedFaculties = safeFacultyStats
		.filter((faculty) => (faculty?.total ?? 0) > 0)
		.sort((a, b) => {
			// Sort by faculty code first
			const aCode = a?.facultyCode ?? "";
			const bCode = b?.facultyCode ?? "";
			if (aCode < bCode) return -1;
			if (aCode > bCode) return 1;
			return 0;
		});

	const formatPercentage = (numerator: number, denominator: number): string => {
		if (denominator === 0) return "0%";
		const percentage = (numerator / denominator) * 100;
		return `${percentage.toFixed(2)}%`;
	};

	const getStatusColor = (value: number, max_scale: number): string => {
		const scale = (value / max_scale) * 100;
		if (scale >= 80) return "text-green-700 dark:text-green-400";
		if (scale >= 60) return "text-yellow-700 dark:text-yellow-400";
		return "text-red-600 dark:text-red-400";
	};

	const getChartData = () => {
		let chartData = [];

		// Add defensive check for departmentStats
		if (!safeDepartmentStats || !Array.isArray(safeDepartmentStats)) {
			return [];
		}

		switch (filterType) {
			case "gender_status":
				chartData = safeDepartmentStats.map((dept) => ({
					department: getMajorName(dept),
					Nam: dept?.male ?? 0,
					Nữ: dept?.female ?? 0,
				}));
				break;
			case "male_only":
				chartData = safeDepartmentStats.map((dept) => ({
					department: getMajorName(dept),
					Nam: dept?.male ?? 0,
				}));
				break;
			case "female_only":
				chartData = safeDepartmentStats.map((dept) => ({
					department: getMajorName(dept),
					Nữ: dept?.female ?? 0,
				}));
				break;
			case "enrollment_status":
				chartData = safeDepartmentStats.map((dept) => ({
					department: getMajorName(dept),
					"Đã hoàn thành": dept?.completed ?? 0,
					"Chưa nhận": dept?.notReceivedAndNotPaid ?? 0,
				}));
				break;
			case "payment_status":
				chartData = safeDepartmentStats.map((dept) => ({
					department: getMajorName(dept),
					"Đã đóng học phí": dept?.paid ?? 0,
					"Chưa đóng học phí": dept?.unpaid ?? 0,
				}));
				break;
			case "paid_only":
				chartData = safeDepartmentStats.map((dept) => ({
					department: getMajorName(dept),
					"Đã đóng học phí": dept?.paid ?? 0,
				}));
				break;
			case "unpaid_only":
				chartData = safeDepartmentStats.map((dept) => ({
					department: getMajorName(dept),
					"Chưa đóng học phí": dept?.unpaid ?? 0,
				}));
				break;
			case "received_only":
				chartData = safeDepartmentStats.map((dept) => ({
					department: getMajorName(dept),
					"Đã hoàn thành": dept?.completed ?? 0,
				}));
				break;
			default:
				chartData = safeDepartmentStats.map((dept) => ({
					department: getMajorName(dept),
					"Đã hoàn thành": dept?.completed ?? 0,
					"Chưa nhận": dept?.notReceivedAndNotPaid ?? 0,
				}));
		}

		// No top-N slice: hiding ngành from the ngành chart hides the very thing a
		// reader opened it for, and the chart already scrolls sideways for width.
		return chartData;
	};

	// Get chart data for advanced view (faculties)
	const getAdvancedChartData = () => {
		// Add defensive check for sortedFaculties
		if (!sortedFaculties || !Array.isArray(sortedFaculties)) {
			return [];
		}

		// No top-N slice: a university has a few dozen khoa at most, and hiding one
		// from the khoa chart hides the very thing this tab exists to compare.
		return sortedFaculties.map((faculty) => {
			const chartEntry: any = {
				facultyName: faculty?.facultyName ?? "Unknown Faculty",
				total: faculty?.total ?? 0,
			};

			switch (filterType) {
				case "gender_status":
					chartEntry.male = faculty?.male ?? 0;
					chartEntry.female = faculty?.female ?? 0;
					break;
				case "male_only":
					chartEntry.male = faculty?.male ?? 0;
					break;
				case "female_only":
					chartEntry.female = faculty?.female ?? 0;
					break;
				case "enrollment_status":
					chartEntry.completed = faculty?.completed ?? 0;
					chartEntry.pending = faculty?.notReceivedAndNotPaid ?? 0;
					break;
				case "payment_status":
					chartEntry.paid = faculty?.paid ?? 0;
					chartEntry.unpaid = faculty?.unpaid ?? 0;
					break;
				case "paid_only":
					chartEntry.paid = faculty?.paid ?? 0;
					break;
				case "unpaid_only":
					chartEntry.unpaid = faculty?.unpaid ?? 0;
					break;
				case "received_only":
					chartEntry.completed = faculty?.completed ?? 0;
					break;
				default:
					chartEntry.completed = faculty?.completed ?? 0;
					chartEntry.pending = faculty?.notReceivedAndNotPaid ?? 0;
					break;
			}

			return chartEntry;
		});
	};

	// Get bars for advanced chart based on filter type
	const getAdvancedChartBars = () => {
		switch (filterType) {
			case "gender_status":
				return [
					<Bar
						{...NO_ANIMATION}
						key="male"
						dataKey="male"
						fill={COLORS[0]}
						name="Nam"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="female"
						dataKey="female"
						fill={COLORS[1]}
						name="Nữ"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "male_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="male"
						dataKey="male"
						fill={COLORS[0]}
						name="Nam"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "female_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="female"
						dataKey="female"
						fill={COLORS[1]}
						name="Nữ"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "enrollment_status":
				return [
					<Bar
						{...NO_ANIMATION}
						key="completed"
						dataKey="completed"
						fill={COLORS[2]}
						name="Đã hoàn thành"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="pending"
						dataKey="pending"
						fill={COLORS[3]}
						name="Chưa tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "payment_status":
				return [
					<Bar
						{...NO_ANIMATION}
						key="paid"
						dataKey="paid"
						fill={COLORS[4]}
						name="Đã đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="unpaid"
						dataKey="unpaid"
						fill={COLORS[5]}
						name="Chưa đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "paid_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="paid"
						dataKey="paid"
						fill={COLORS[4]}
						name="Đã đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "unpaid_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="unpaid"
						dataKey="unpaid"
						fill={COLORS[5]}
						name="Chưa đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "received_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="completed"
						dataKey="completed"
						fill={COLORS[2]}
						name="Đã hoàn thành"
						label={BAR_VALUE_LABEL}
					/>,
				];
			default:
				return [
					<Bar
						{...NO_ANIMATION}
						key="completed"
						dataKey="completed"
						fill={COLORS[0]}
						name="Đã hoàn thành"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="pending"
						dataKey="pending"
						fill={COLORS[1]}
						name="Chưa tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
				];
		}
	};

	return (
		<TabsContent value={view}>
			<div ref={departmentRef} className="space-y-6">
				<div className="flex justify-between items-center">
					<h2 className="text-2xl font-semibold">{heading.title}</h2>
					<Button
						onClick={() => downloadChartPng(departmentRef, heading.filename)}
						variant="outline"
						size="sm"
						className="flex items-center gap-2"
					>
						<Download className="h-4 w-4" />
						Tải PNG
					</Button>
				</div>

				{view === "majors" ? (
					<div className="space-y-6">
						<StatisticsFilterControls
							filterType={filterType}
							onFilterChange={handleFilterChange}
							showChartLimit={false}
							filters={filters}
							onFiltersChange={onFiltersChange}
							showFilters={true}
						/>

						<ChartCard
							title="Thống kê theo ngành"
							filename="thong-ke-theo-nganh-hoc"
							description={`Hiển thị tất cả ${getChartData().length} ngành`}
						>
							{/* Cuộn ngang thay vì bóp biểu đồ: tên ngành đầy đủ cần bề ngang thật
								    cho mỗi cột, và cách duy nhất để giữ nguyên vùng vẽ là để biểu đồ
								    rộng hơn khung rồi cho nó cuộn. Vùng vẽ vẫn cao 400px như trước. */}
							<div className="overflow-x-auto">
								<div style={{ minWidth: chartMinWidth(getChartData().length) }}>
									<ResponsiveContainer width="100%" height={400 + CATEGORY_AXIS_EXTRA_HEIGHT}>
										<BarChart
											data={getChartData()}
											margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
										>
											<CartesianGrid strokeDasharray="3 3" />
											{/* `interval={0}` để không hạng mục nào bị bỏ nhãn: recharts mặc
												    định giấu bớt nhãn khi thấy chật, và ở đây "chật" không còn
												    đúng nữa vì đã có bề ngang từ việc cuộn. */}
											<XAxis
												dataKey="department"
												interval={0}
												tick={<WrappedCategoryTick />}
												height={CATEGORY_AXIS_HEIGHT}
											/>
											<YAxis />
											<Tooltip />
											<Legend />
											{Object.keys(getChartData()[0] || {})
												.filter((key) => key !== "department")
												.map((key, index) => (
													<Bar
														{...NO_ANIMATION}
														key={key}
														dataKey={key}
														fill={COLORS[index % COLORS.length]}
														label={BAR_VALUE_LABEL}
													/>
												))}
										</BarChart>
									</ResponsiveContainer>
								</div>
							</div>
						</ChartCard>

						<ProgressList
							title="Tiến độ theo ngành đào tạo"
							description={`${safeDepartmentStats.length} ngành`}
							remainderLabel="Chưa nộp hồ sơ, chưa đóng phí"
							sortBy={sortBy}
							onSortByChange={setSortBy}
							rows={sortRowsLocally(
								safeDepartmentStats.map((dept: StatisticsByDepartment) => ({
									id: dept?.departmentCode ?? "unknown",
									label: getMajorName(dept),
									sublabel: `Mã ngành: ${dept?.departmentCode ?? "N/A"}`,
									total: dept?.total ?? 0,
									primary: dept?.received ?? 0,
									segments: [
										{
											key: "received",
											label: "Đã tiếp nhận",
											value: dept?.received ?? 0,
											className: "bg-green-600",
										},
										{
											key: "paidButNotReceived",
											label: "Đã đóng phí, chưa tiếp nhận",
											value: dept?.paidButNotReceived ?? 0,
											className: "bg-green-300 dark:bg-green-800",
										},
									],
									details: [
										{
											label: "Tổng",
											value: dept?.total ?? 0,
											className: "text-blue-600 dark:text-blue-400",
										},
										{
											label: "Đã hoàn thành",
											value: dept?.completed ?? 0,
											className: "text-green-700 dark:text-green-400",
										},
										{
											label: "Tiếp nhận chưa đóng phí",
											value: dept?.receivedButUnpaid ?? 0,
											className: "text-yellow-700 dark:text-yellow-400",
										},
										{
											label: "Đã đóng phí chưa tiếp nhận",
											value: dept?.paidButNotReceived ?? 0,
											className: "text-orange-700 dark:text-orange-400",
										},
										{
											label: "Chưa nhận",
											value: dept?.notReceivedAndNotPaid ?? 0,
											className: "text-orange-700 dark:text-orange-400",
										},
										{
											label: "Đã đóng học phí",
											value: dept?.paid ?? 0,
											className: "text-purple-600 dark:text-purple-400",
										},
										{
											label: "Nam",
											value: dept?.male ?? 0,
											className: "text-blue-600 dark:text-blue-400",
										},
										{
											label: "Nữ",
											value: dept?.female ?? 0,
											className: "text-pink-600 dark:text-pink-400",
										},
									],
								})),
								sortBy,
							)}
						/>
					</div>
				) : (
					<div className="space-y-6">
						<div>
							<p className="text-muted-foreground">Phân tích ngành học theo khoa đào tạo</p>
						</div>

						<StatisticsFilterControls
							filterType={filterType}
							onFilterChange={handleFilterChange}
							showChartLimit={false}
							filters={filters}
							onFiltersChange={onFiltersChange}
							showFilters={true}
						/>

						{/* Bar Chart */}
						<ChartCard
							title="Thống kê theo khoa"
							filename="thong-ke-theo-khoa"
							description={`Hiển thị tất cả ${getAdvancedChartData().length} khoa`}
						>
							{/* Flat labels, as on the "Đơn giản" tab: khoa names wrap instead of
								    rotating 45 degrees, and the chart scrolls when it needs the width. */}
							<div className="overflow-x-auto">
								<div style={{ minWidth: chartMinWidth(getAdvancedChartData().length) }}>
									<ResponsiveContainer width="100%" height={400 + CATEGORY_AXIS_EXTRA_HEIGHT}>
										<BarChart
											data={getAdvancedChartData()}
											margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
										>
											<CartesianGrid strokeDasharray="3 3" />
											<XAxis
												dataKey="facultyName"
												interval={0}
												tick={<WrappedCategoryTick />}
												height={CATEGORY_AXIS_HEIGHT}
											/>
											<YAxis />
											<Tooltip />
											<Legend />
											{getAdvancedChartBars()}
										</BarChart>
									</ResponsiveContainer>
								</div>
							</div>
						</ChartCard>

						{/* Faculty Statistics Table */}
						<Card className="mt-4">
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle className="flex items-center gap-2 text-base md:text-lg">
										<GraduationCap className="h-4 w-4 md:h-5 md:w-5" />
										Chi tiết theo từng khoa
									</CardTitle>
									<Button
										onClick={toggleAllFaculties}
										variant="outline"
										size="sm"
										className="flex items-center gap-1 md:gap-2 text-xs md:text-sm"
									>
										{expandedFaculties.size === sortedFaculties.length ? (
											<>
												<ChevronDown className="h-3 w-3 md:h-4 md:w-4" />
												<span className="hidden sm:inline">Thu gọn tất cả</span>
												<span className="sm:hidden">Thu gọn</span>
											</>
										) : (
											<>
												<ChevronRight className="h-3 w-3 md:h-4 md:w-4" />
												<span className="hidden sm:inline">Mở rộng tất cả</span>
												<span className="sm:hidden">Mở rộng</span>
											</>
										)}
									</Button>
								</div>
							</CardHeader>
							<CardContent>
								<div className="overflow-x-auto">
									<table className="w-full border-collapse border border-border text-xs md:text-sm">
										<thead>
											<tr className="bg-muted">
												<th className="border border-border p-2 md:p-3 text-left font-medium">
													Khoa
												</th>
												<th className="border border-border p-2 md:p-3 text-center font-medium w-20">
													Tổng SV
												</th>
												<th className="border border-border p-2 md:p-3 text-center font-medium w-20">
													Hoàn thành
												</th>
												<th className="border border-border p-2 md:p-3 text-center font-medium w-20">
													Tiếp nhận Chưa đóng phí
												</th>
												<th className="border border-border p-2 md:p-3 text-center font-medium w-20">
													Chưa tiếp nhận Đã đóng phí
												</th>
												<th className="border border-border p-2 md:p-3 text-center font-medium w-20">
													Chưa nhận
												</th>
												<th className="border border-border p-2 md:p-3 text-center font-medium w-20">
													Nam/Nữ
												</th>
												<th className="border border-border p-2 md:p-3 text-center font-medium">
													Tiến độ
												</th>
											</tr>
										</thead>
										<tbody>
											{sortedFaculties.map((faculty, index) => {
												const receivedRate =
													((faculty?.received ?? 0) / (faculty?.total ?? 1)) * 100;
												const paidRate = ((faculty?.paid ?? 0) / (faculty?.total ?? 1)) * 100;
												const isExpanded = expandedFaculties.has(faculty?.facultyCode ?? "");

												return (
													<>
														{/* Faculty Header Row */}
														<tr
															key={faculty.facultyCode}
															className={index % 2 === 0 ? "bg-card" : "bg-muted"}
														>
															<td className="border border-border p-2 md:p-3">
																<div className="flex items-center gap-1 md:gap-2">
																	<button
																		onClick={() => toggleFacultyExpansion(faculty.facultyCode)}
																		className="flex-shrink-0 p-1 hover:bg-accent rounded transition-colors"
																	>
																		{isExpanded ? (
																			<ChevronDown className="h-3 w-3 md:h-4 md:w-4" />
																		) : (
																			<ChevronRight className="h-3 w-3 md:h-4 md:w-4" />
																		)}
																	</button>
																	<div className="min-w-0">
																		<div className="font-medium text-foreground text-xs md:text-sm break-words">
																			{faculty.facultyName}
																		</div>
																		<div className="text-xs text-muted-foreground break-words">
																			{faculty.facultyCode}
																		</div>
																		<div className="text-xs text-muted-foreground">
																			{departmentsOfFaculty(faculty.facultyCode).length} ngành
																		</div>
																	</div>
																</div>
															</td>
															<td className="border border-border p-2 md:p-3 text-center w-20">
																<div className="text-sm md:text-lg font-semibold">
																	{(faculty?.total ?? 0).toLocaleString()}
																</div>
															</td>
															<td className="border border-border p-2 md:p-3 text-center w-20">
																<div className="space-y-1">
																	<div className="text-sm md:text-lg font-semibold">
																		{(faculty?.completed ?? 0).toLocaleString()}
																	</div>
																	<div
																		className={`text-xs md:text-sm ${getStatusColor(receivedRate, 100)}`}
																	>
																		{formatPercentage(faculty?.completed ?? 0, faculty?.total ?? 0)}
																	</div>
																</div>
															</td>
															<td className="border border-border p-2 md:p-3 text-center w-20">
																<div className="space-y-1">
																	<div className="text-sm md:text-lg font-semibold">
																		{(faculty?.receivedButUnpaid ?? 0).toLocaleString()}
																	</div>
																	<div
																		className={`text-xs md:text-sm ${getStatusColor(((faculty?.receivedButUnpaid ?? 0) / (faculty?.total ?? 1)) * 100, 100)}`}
																	>
																		{formatPercentage(
																			faculty?.receivedButUnpaid ?? 0,
																			faculty?.total ?? 0,
																		)}
																	</div>
																</div>
															</td>
															<td className="border border-border p-2 md:p-3 text-center w-20">
																<div className="space-y-1">
																	<div className="text-sm md:text-lg font-semibold">
																		{(faculty?.paidButNotReceived ?? 0).toLocaleString()}
																	</div>
																	<div
																		className={`text-xs md:text-sm ${getStatusColor(((faculty?.paidButNotReceived ?? 0) / (faculty?.total ?? 1)) * 100, 100)}`}
																	>
																		{formatPercentage(
																			faculty?.paidButNotReceived ?? 0,
																			faculty?.total ?? 0,
																		)}
																	</div>
																</div>
															</td>
															<td className="border border-border p-2 md:p-3 text-center w-20">
																<div className="space-y-1">
																	<div className="text-sm md:text-lg font-semibold">
																		{(faculty?.notReceivedAndNotPaid ?? 0).toLocaleString()}
																	</div>
																	<div
																		className={`text-xs md:text-sm ${getStatusColor(((faculty?.notReceivedAndNotPaid ?? 0) / (faculty?.total ?? 1)) * 100, 100)}`}
																	>
																		{formatPercentage(
																			faculty?.notReceivedAndNotPaid ?? 0,
																			faculty?.total ?? 0,
																		)}
																	</div>
																</div>
															</td>
															<td className="border border-border p-2 md:p-3 text-center w-20">
																<div className="space-y-1">
																	<div className="text-blue-600 dark:text-blue-400 font-medium text-xs md:text-sm">
																		{faculty?.male ?? 0}
																	</div>
																	<div className="text-pink-600 dark:text-pink-400 font-medium text-xs md:text-sm">
																		{faculty?.female ?? 0}
																	</div>
																	<div className="text-xs text-muted-foreground">
																		{formatPercentage(faculty?.male ?? 0, faculty?.total ?? 0)} /{" "}
																		{formatPercentage(faculty?.female ?? 0, faculty?.total ?? 0)}
																	</div>
																</div>
															</td>
															<td className="border border-border p-2 md:p-3">
																<div className="space-y-1">
																	<div className="flex items-center justify-between text-xs md:text-sm">
																		<span>Hoàn thành:</span>
																		<span className={getStatusColor(receivedRate, 100)}>
																			{receivedRate.toFixed(1)}%
																		</span>
																	</div>
																	<div className="w-full bg-secondary rounded-full h-1.5 md:h-2">
																		<div
																			className="bg-blue-600 h-1.5 md:h-2 rounded-full transition-all duration-300"
																			style={{ width: `${Math.min(receivedRate, 100)}%` }}
																		></div>
																	</div>
																	<div className="flex items-center justify-between text-xs md:text-sm">
																		<span>Đóng phí:</span>
																		<span className={getStatusColor(paidRate, 100)}>
																			{paidRate.toFixed(1)}%
																		</span>
																	</div>
																	<div className="w-full bg-secondary rounded-full h-1.5 md:h-2">
																		<div
																			className="bg-green-600 h-1.5 md:h-2 rounded-full transition-all duration-300"
																			style={{ width: `${Math.min(paidRate, 100)}%` }}
																		></div>
																	</div>
																</div>
															</td>
														</tr>

														{/* Department Detail Rows - Only show if faculty is expanded */}
														{isExpanded &&
															departmentsOfFaculty(faculty?.facultyCode ?? "").map((dept) => {
																const deptCompletedRate =
																	(dept?.total ?? 0) > 0
																		? ((dept?.completed ?? 0) / (dept?.total ?? 1)) * 100
																		: 0;
																const deptReceivedUnpaidRate =
																	(dept?.total ?? 0) > 0
																		? ((dept?.receivedButUnpaid ?? 0) / (dept?.total ?? 1)) * 100
																		: 0;
																const deptPaidNotReceivedRate =
																	(dept?.total ?? 0) > 0
																		? ((dept?.paidButNotReceived ?? 0) / (dept?.total ?? 1)) * 100
																		: 0;
																const deptNotReceivedNotPaidRate =
																	(dept?.total ?? 0) > 0
																		? ((dept?.notReceivedAndNotPaid ?? 0) / (dept?.total ?? 1)) *
																			100
																		: 0;

																return (
																	<tr
																		key={`${faculty?.facultyCode ?? "unknown"}-${dept?.departmentCode ?? "unknown"}`}
																		className="bg-blue-50 dark:bg-blue-950"
																	>
																		<td className="border border-border p-2 md:p-3 pl-6 md:pl-12">
																			<div className="min-w-0">
																				<div className="font-medium text-foreground text-xs md:text-sm break-words">
																					{getMajorName(dept)}
																				</div>
																				<div className="text-xs text-muted-foreground break-words">
																					Mã: {dept?.departmentCode ?? "N/A"}
																				</div>
																			</div>
																		</td>
																		<td className="border border-border p-2 md:p-3 text-center w-20">
																			<div className="text-sm md:text-base font-medium">
																				{(dept?.total ?? 0).toLocaleString()}
																			</div>
																		</td>
																		<td className="border border-border p-2 md:p-3 text-center w-20">
																			<div className="space-y-1">
																				<div className="text-sm md:text-base font-medium">
																					{(dept?.completed ?? 0).toLocaleString()}
																				</div>
																				<div
																					className={`text-xs md:text-sm ${getStatusColor(deptCompletedRate, 100)}`}
																				>
																					{formatPercentage(dept?.completed ?? 0, dept?.total ?? 0)}
																				</div>
																			</div>
																		</td>
																		<td className="border border-border p-2 md:p-3 text-center w-20">
																			<div className="space-y-1">
																				<div className="text-sm md:text-base font-medium">
																					{(dept?.receivedButUnpaid ?? 0).toLocaleString()}
																				</div>
																				<div
																					className={`text-xs md:text-sm ${getStatusColor(100 - deptReceivedUnpaidRate, 100)}`}
																				>
																					{formatPercentage(
																						dept?.receivedButUnpaid ?? 0,
																						dept?.total ?? 0,
																					)}
																				</div>
																			</div>
																		</td>
																		<td className="border border-border p-2 md:p-3 text-center w-20">
																			<div className="space-y-1">
																				<div className="text-sm md:text-base font-medium">
																					{(dept?.paidButNotReceived ?? 0).toLocaleString()}
																				</div>
																				<div
																					className={`text-xs md:text-sm ${getStatusColor(100 - deptPaidNotReceivedRate, 100)}`}
																				>
																					{formatPercentage(
																						dept?.paidButNotReceived ?? 0,
																						dept?.total ?? 0,
																					)}
																				</div>
																			</div>
																		</td>
																		<td className="border border-border p-2 md:p-3 text-center w-20">
																			<div className="space-y-1">
																				<div className="text-sm md:text-base font-medium">
																					{(dept?.notReceivedAndNotPaid ?? 0).toLocaleString()}
																				</div>
																				<div
																					className={`text-xs md:text-sm ${getStatusColor(100 - deptNotReceivedNotPaidRate, 100)}`}
																				>
																					{formatPercentage(
																						dept?.notReceivedAndNotPaid ?? 0,
																						dept?.total ?? 0,
																					)}
																				</div>
																			</div>
																		</td>
																		<td className="border border-border p-2 md:p-3 text-center w-20">
																			<div className="space-y-1">
																				<div className="text-blue-600 dark:text-blue-400 font-medium text-xs md:text-sm">
																					{dept?.male ?? 0}
																				</div>
																				<div className="text-pink-600 dark:text-pink-400 font-medium text-xs md:text-sm">
																					{dept?.female ?? 0}
																				</div>
																				<div className="text-xs text-muted-foreground">
																					{formatPercentage(dept?.male ?? 0, dept?.total ?? 0)} /{" "}
																					{formatPercentage(dept?.female ?? 0, dept?.total ?? 0)}
																				</div>
																			</div>
																		</td>
																		<td className="border border-border p-2 md:p-3">
																			<div className="space-y-1">
																				<div className="flex items-center justify-between text-xs">
																					<span>HT:</span>
																					<span className={getStatusColor(deptCompletedRate, 100)}>
																						{deptCompletedRate.toFixed(1)}%
																					</span>
																				</div>
																				<div className="w-full bg-secondary rounded-full h-1">
																					<div
																						className="bg-blue-600 h-1 rounded-full transition-all duration-300"
																						style={{
																							width: `${Math.min(deptCompletedRate, 100)}%`,
																						}}
																					></div>
																				</div>
																				<div className="flex items-center justify-between text-xs">
																					<span>Đóng phí:</span>
																					<span
																						className={getStatusColor(
																							(((dept?.total ?? 0) -
																								(dept?.notReceivedAndNotPaid ?? 0)) /
																								(dept?.total ?? 1)) *
																								100,
																							100,
																						)}
																					>
																						{(
																							(((dept?.total ?? 0) -
																								(dept?.notReceivedAndNotPaid ?? 0)) /
																								(dept?.total ?? 1)) *
																							100
																						).toFixed(1)}
																						%
																					</span>
																				</div>
																				<div className="w-full bg-secondary rounded-full h-1">
																					<div
																						className="bg-green-600 h-1 rounded-full transition-all duration-300"
																						style={{
																							width: `${Math.min((((dept?.total ?? 0) - (dept?.notReceivedAndNotPaid ?? 0)) / (dept?.total ?? 1)) * 100, 100)}%`,
																						}}
																					></div>
																				</div>
																			</div>
																		</td>
																	</tr>
																);
															})}
													</>
												);
											})}
										</tbody>
									</table>
								</div>
							</CardContent>
						</Card>

						{/* Bảng xếp hạng theo khoa — hiện ĐỦ mọi khoa, không cắt top 5.
						    Cắt 5 làm mất đúng thông tin người quản lý cần: khoa đang chậm nhất.
						    Số khoa là hữu hạn và nhỏ (10 khoa trên dữ liệu thật), nên danh sách
						    đầy đủ vẫn đọc được trong một thẻ mà không cần phân trang. */}
						<div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-base md:text-lg">
										<Trophy className="h-4 w-4 md:h-5 md:w-5 text-yellow-700 dark:text-yellow-400" />
										BXH khoa tỷ lệ hoàn thành
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-2 md:space-y-3">
										{/* `[...]` trước `sort`: `sortedFaculties` được dùng lại ở bảng chi
										    tiết phía trên và ở thẻ BXH bên cạnh, mà `sort` thì đổi CHÍNH mảng
										    đó. Sắp xếp trực tiếp là để hai thẻ giành nhau một thứ tự. */}
										{[...sortedFaculties]
											.sort(
												(a, b) =>
													(b?.completed ?? 0) / (b?.total ?? 1) -
													(a?.completed ?? 0) / (a?.total ?? 1),
											)
											.map((faculty, index) => {
												const rate = ((faculty?.completed ?? 0) / (faculty?.total ?? 1)) * 100;
												return (
													<div
														key={`top-received-${faculty?.facultyCode ?? "unknown"}-${index}`}
														className="flex items-center justify-between p-2 md:p-3 bg-muted rounded-lg"
													>
														<div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
															<span className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 bg-yellow-100 dark:bg-yellow-950 text-yellow-800 dark:text-yellow-300 rounded-full font-bold text-xs md:text-sm flex-shrink-0">
																{index + 1}
															</span>
															<div className="min-w-0 flex-1">
																<div className="font-medium text-sm md:text-base break-words">
																	{faculty?.facultyName ?? "Unknown Faculty"}
																</div>
																<div className="text-xs md:text-sm text-muted-foreground">
																	{faculty?.completed ?? 0}/{faculty?.total ?? 0} sinh viên
																</div>
															</div>
														</div>
														<div
															className={`text-sm md:text-lg font-bold ${getStatusColor(rate, 100)} flex-shrink-0 ml-2`}
														>
															{rate.toFixed(1)}%
														</div>
													</div>
												);
											})}
									</div>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-base md:text-lg">
										<TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-green-700 dark:text-green-400" />
										BXH khoa tỷ lệ hoàn thành học phí
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-2 md:space-y-3">
										{/* Xem ghi chú ở thẻ BXH bên cạnh: sao mảng trước khi `sort`, và hiện
										    đủ mọi khoa thay vì cắt top 5. */}
										{[...sortedFaculties]
											.sort(
												(a, b) =>
													(b?.paid ?? 0) / (b?.total ?? 1) - (a?.paid ?? 0) / (a?.total ?? 1),
											)
											.map((faculty, index) => {
												const paidRate = ((faculty?.paid ?? 0) / (faculty?.total ?? 1)) * 100;
												return (
													<div
														key={`top-paid-${faculty?.facultyCode ?? "unknown"}-${index}`}
														className="flex items-center justify-between p-2 md:p-3 bg-muted rounded-lg"
													>
														<div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
															<span className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 bg-green-100 dark:bg-green-950 text-green-800 dark:text-green-300 rounded-full font-bold text-xs md:text-sm flex-shrink-0">
																{index + 1}
															</span>
															<div className="min-w-0 flex-1">
																<div className="font-medium text-sm md:text-base break-words">
																	{faculty?.facultyName ?? "Unknown Faculty"}
																</div>
																<div className="text-xs md:text-sm text-muted-foreground">
																	{faculty?.total ?? 0} sinh viên
																</div>
															</div>
														</div>
														<div className="text-sm md:text-lg font-bold text-green-700 dark:text-green-400 flex-shrink-0 ml-2">
															{paidRate.toFixed(1)}%
														</div>
													</div>
												);
											})}
									</div>
								</CardContent>
							</Card>
						</div>
					</div>
				)}
			</div>
		</TabsContent>
	);
}
